import { Classes, PanelProps } from '@blueprintjs/core';
import classNames from 'classnames';
import * as React from 'react';

import {
  TEST_IDS,
  getPublishProjectDrawerResourceRowTestId,
} from 'src/constants/testIds';
import { CSVMappingRow } from 'src/containers/PublishProjectDrawer/CsvMappingRow';
import {
  useDefaultCsvMappingInfo,
  useDefaultVirtualGraphMappingInfo,
  usePublish,
  useValidateNamedGraphIri,
  useValidateVirtualGraphName,
} from 'src/containers/PublishProjectDrawer/hooks';
import {
  OpenInExplorerPanel,
  OpenInExplorerPanelProps,
} from 'src/containers/PublishProjectDrawer/OpenInExplorerPanel';
import { PublishProjectDrawerFooter } from 'src/containers/PublishProjectDrawer/PublishProjectDrawerFooter';
import { PublishProjectDrawerStepper } from 'src/containers/PublishProjectDrawer/PublishProjectDrawerStepper';
import {
  ConnectionInfo,
  CreationType,
  CsvMappingsInfo,
  ModelInfo,
  RulesInfo,
  SensitivePropertyGroupsInfo,
  VirtualGraphMappingsInfo,
} from 'src/containers/PublishProjectDrawer/types';
import { VirtualGraphMappingRow } from 'src/containers/PublishProjectDrawer/VirtualGraphMappingRow';
import { connectionSelector } from 'src/selectors/connection';
import { diagramSnapshotSelector } from 'src/selectors/diagramSnapshot';
import { isStardogFeatureSupportedSelector } from 'src/selectors/isStardogFeatureSupported';
import { projectSelector } from 'src/selectors/project';
import { projectFileUploadsByIdSelector } from 'src/selectors/projectFileUploadsById';
import { projectVirtualGraphsByIdSelector } from 'src/selectors/projectVirtualGraphsById';
import * as copy from 'src/templates/copy';
import { getValidMappingsByResource } from 'src/utils/export/validateMappingsByResource';
import { useDidValueChange } from 'src/utils/hooks/useDidValueChange';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

export type MappingConfigPanelProps = {
  connectionInfo: ConnectionInfo;
  isPublishingRules: boolean;
  isPublishingSPG: boolean;
  modelInfo: ModelInfo;
  ruleInfo: RulesInfo;
  spgInfo: SensitivePropertyGroupsInfo;
};

export const MappingConfigPanel: React.VFC<
  PanelProps<MappingConfigPanelProps>
> = ({
  closePanel,
  isPublishingRules,
  isPublishingSPG,
  connectionInfo,
  modelInfo,
  ruleInfo,
  spgInfo,
  openPanel,
}) => {
  const { databaseId } = connectionInfo;
  const { projectId } = useRouterParams();
  const project = useReduxSelector(projectSelector(projectId));
  const publish = usePublish();
  const getDefaultCsvMappingInfo = useDefaultCsvMappingInfo(databaseId);
  const getDefaultVgMappingInfo = useDefaultVirtualGraphMappingInfo(databaseId);
  const connection = useReduxSelector(connectionSelector);
  const isCatalogEnabled = useReduxSelector(
    isStardogFeatureSupportedSelector('isCatalogSupported', connection)
  );

  const diagramSnapshot = useReduxSelector(diagramSnapshotSelector(projectId));
  const virtualGraphs = useReduxSelector(
    projectVirtualGraphsByIdSelector(projectId)
  );
  const fileUploads = useReduxSelector(
    projectFileUploadsByIdSelector(projectId)
  );

  const validMappingsByResource = diagramSnapshot
    ? getValidMappingsByResource(diagramSnapshot?.model).classMappingsByResource
    : {};

  const initialVgMappingInfo: VirtualGraphMappingsInfo = Object.fromEntries(
    Object.values(virtualGraphs)
      .filter((virtualGraph) => validMappingsByResource[virtualGraph.id])
      .map((virtualGraph) => [
        virtualGraph.id,
        getDefaultVgMappingInfo(virtualGraph),
      ])
  );
  const [vgMappingInfo, setVgMappingInfo] =
    React.useState(initialVgMappingInfo);

  const initialCsvMappingInfo: CsvMappingsInfo = Object.fromEntries(
    Object.values(fileUploads)
      .filter((fileUpload) => validMappingsByResource[fileUpload.id])
      .map((fileUpload) => [
        fileUpload.id,
        getDefaultCsvMappingInfo(fileUpload),
      ])
  );
  const [csvMappingInfo, setCsvMappingInfo] = React.useState(
    initialCsvMappingInfo
  );

  const didProjectChange = useDidValueChange(project);
  React.useEffect(() => {
    if (didProjectChange) {
      setVgMappingInfo(initialVgMappingInfo);
      setCsvMappingInfo(initialCsvMappingInfo);
    }
  }, [didProjectChange, initialCsvMappingInfo, initialVgMappingInfo]);

  const validateNamedGraph = useValidateNamedGraphIri(databaseId);
  const validateVirtualGraphName = useValidateVirtualGraphName();
  const nextButtonError = React.useMemo(() => {
    let csvError = '';
    if (
      Object.entries(csvMappingInfo).find(([id, csvMapping]) => {
        const fileUpload = fileUploads[id];
        if (csvMapping.creationType === CreationType.NEW) {
          csvError = validateNamedGraph(csvMapping.namedGraphIri);
        } else if (!csvMapping.namedGraphIri) {
          csvError =
            copy.components.publishProjectDrawer.validation.namedGraph.required;
        }

        if (!csvError && fileUpload?.partial && !csvMapping.fileOptions) {
          csvError =
            copy.components.publishProjectDrawer.validation.csvUpload.required;
        }

        if (csvError) {
          csvError =
            copy.components.publishProjectDrawer.validation.csvUpload.incomplete(
              fileUpload?.name || '',
              csvError
            );
        }
        return Boolean(csvError);
      })
    ) {
      return csvError;
    }

    let vgError = '';
    if (
      Object.entries(vgMappingInfo).find(([id, vgMapping]) => {
        const virtualGraph = virtualGraphs[id];
        if (vgMapping.creationType === CreationType.NEW) {
          vgError = validateVirtualGraphName(vgMapping.virtualGraphName);
        } else if (!vgMapping.virtualGraphName) {
          vgError =
            copy.components.publishProjectDrawer.validation.virtualGraph
              .required;
        }

        if (vgError) {
          vgError =
            copy.components.publishProjectDrawer.validation.csvUpload.incomplete(
              virtualGraph?.name || '',
              vgError
            );
        }
        return Boolean(vgError);
      })
    ) {
      return vgError;
    }

    return '';
  }, [
    csvMappingInfo,
    fileUploads,
    validateNamedGraph,
    validateVirtualGraphName,
    vgMappingInfo,
    virtualGraphs,
  ]);

  const handleNext = () => {
    const props: OpenInExplorerPanelProps = {
      connectionInfo,
      isPublishingRules,
      modelInfo,
      ruleInfo,
      vgMappingInfo,
    };
    openPanel({ renderPanel: OpenInExplorerPanel, props });
    publish({
      connectionInfo,
      csvMappingInfo,
      isPublishingRules,
      isPublishingSPG,
      modelInfo,
      projectId,
      ruleInfo,
      spgInfo,
      vgMappingInfo,
    });
  };

  // Check that stardog version supports features of virtual graphs
  const warningCallout = React.useMemo(() => {
    if (isCatalogEnabled) {
      return '';
    }
    const hasInvalidMapping = Object.values(virtualGraphs).some(
      (virtualGraph) => {
        const connectionFrom = virtualGraph.from;
        return typeof connectionFrom !== 'string' && connectionFrom.catalog;
      }
    );
    return hasInvalidMapping
      ? copy.components.publishProjectDrawer.mappingConfiguration.virtualGraph
          .versionWarning
      : '';
  }, [isCatalogEnabled, virtualGraphs]);

  return (
    <>
      <div
        className={Classes.DRAWER_BODY}
        data-testid={TEST_IDS.publishProjectDrawer.mapping.container}
      >
        <PublishProjectDrawerStepper active="mapping" />
        <div>
          <div
            className={classNames(
              'sd-resource-mapping-header',
              'sd-resource-mapping-grid'
            )}
          >
            <span>
              {
                copy.components.publishProjectDrawer.mappingConfiguration
                  .columnHeader.resource
              }
            </span>
            <span>
              {
                copy.components.publishProjectDrawer.mappingConfiguration
                  .columnHeader.mappings
              }
            </span>
            <span>
              {
                copy.components.publishProjectDrawer.mappingConfiguration
                  .columnHeader.configuration
              }
            </span>
          </div>
          {Object.values(virtualGraphs).map((virtualGraph) => {
            const mappings = validMappingsByResource[virtualGraph.id];
            return mappings ? (
              <VirtualGraphMappingRow
                key={virtualGraph.id}
                databaseId={databaseId}
                info={vgMappingInfo[virtualGraph.id]}
                initialInfo={initialVgMappingInfo[virtualGraph.id]}
                mappings={mappings}
                setInfo={(info) =>
                  setVgMappingInfo({
                    ...vgMappingInfo,
                    [virtualGraph.id]: info,
                  })
                }
                testId={getPublishProjectDrawerResourceRowTestId(
                  virtualGraph.id
                )}
                virtualGraph={virtualGraph}
              />
            ) : null;
          })}
          {Object.values(fileUploads).map((fileUpload) => {
            const mappings = validMappingsByResource[fileUpload.id];
            return mappings ? (
              <CSVMappingRow
                key={fileUpload.id}
                databaseId={databaseId}
                fileUpload={fileUpload}
                info={csvMappingInfo[fileUpload.id]}
                initialInfo={initialCsvMappingInfo[fileUpload.id]}
                mappings={mappings}
                setInfo={(info) =>
                  setCsvMappingInfo({
                    ...csvMappingInfo,
                    [fileUpload.id]: info,
                  })
                }
                testId={getPublishProjectDrawerResourceRowTestId(fileUpload.id)}
              />
            ) : null;
          })}
        </div>
      </div>
      <PublishProjectDrawerFooter
        handleBack={closePanel}
        handleNext={handleNext}
        nextError={nextButtonError}
        nextTestId={TEST_IDS.publishProjectDrawer.footer.publishButton}
        nextText={copy.components.publishProjectDrawer.footer.publish}
        warningCallout={warningCallout}
      />
    </>
  );
};
